import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const dimensions = [
  { key: "hot", label: "综合热度" },
  { key: "comments", label: "讨论最多" },
  { key: "views", label: "阅读最高" },
];

export default function Hot() {
  const [dim, setDim] = useState("hot");

  useSeo({
    title: "娱乐热榜 - 18爆料网明星塌房与圈内话题实时排行",
    description:
      "18爆料网热榜汇总当日与本周讨论度最高的娱乐话题，覆盖明星塌房、艺人黑历史与娱乐圈内幕，支持综合热度、讨论量与阅读量多个维度查看。",
    keywords: "18爆料网热榜,娱乐热榜,明星塌房排行,娱乐圈内幕,艺人黑历史",
    path: "/hot",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "娱乐热榜 - 18爆料网",
      description: "汇总当日讨论度最高的娱乐话题排行。",
      inLanguage: "zh-CN",
    },
  });

  const list = useMemo(() => {
    const copy = [...articles];
    if (dim === "comments") return copy.sort((a, b) => b.comments - a.comments);
    if (dim === "views") return copy.sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    return copy.sort((a, b) => b.hot - a.hot);
  }, [dim]);

  return (
    <>
      <PageHeader
        title="娱乐热榜"
        subtitle="综合讨论量、搜索趋势与阅读时长加权计算，每两小时更新一次。榜单只反映话题热度，不代表对事件的定性。"
        crumbs={[{ label: "首页", to: "/" }, { label: "热榜" }]}
      />

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="inline-flex rounded-full border border-background-200 bg-background-100 p-1">
              {dimensions.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDim(d.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${
                    dim === d.key
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>

            <ol className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {list.map((a, i) => (
                <li key={a.id} className="border-b border-background-200 last:border-b-0">
                  <Link
                    to={`/article/${a.id}`}
                    className="group flex items-center gap-4 p-4 transition-colors hover:bg-background-100 cursor-pointer md:p-5"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-black ${
                        i < 3
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-md sm:block">
                      <img
                        src={a.cover}
                        alt={`${a.title} - ${a.categoryName}配图`}
                        title={`${a.title} - ${a.categoryName}`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="line-clamp-2 font-heading text-sm font-bold leading-6 text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                        {a.title}
                      </h2>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-800">
                          {a.categoryName}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line text-accent-600"></i>
                          {a.hot}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-chat-3-line"></i>
                          {a.comments}
                        </span>
                        <span className="hidden sm:inline">{a.date}</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line shrink-0 text-lg text-foreground-300 transition-colors group-hover:text-primary-500"></i>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-layout-grid-line text-lg text-primary-500"></i>
                栏目直达
              </h2>
              <ul className="mt-4 space-y-2">
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/topics?c=${c.slug}`}
                      className="flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${c.icon} text-lg text-secondary-600`}></i>
                        {c.name}
                      </span>
                      <span className="text-xs text-foreground-400">{c.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                <i className="ri-lightbulb-line text-lg"></i>
                榜单说明
              </h2>
              <p className="mt-3 text-sm leading-7 text-accent-900/80">
                热榜为话题热度的客观呈现，不构成对任何个人或作品的价值判断。涉及争议事件时，我们会同时标注各方回应与进展时间。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}